import React, { useState } from 'react'
import styled from 'styled-components'
import Text from 'components/_UI/Text'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Modal from 'components/_UI/Modal'
import { ChevronUp, ChevronDown, Edit, Trash2, PlusSquare } from 'lucide-react'
import { useEvent } from 'context/events/Event'
import sponsorSectionsApi from 'api/requests/sponsorSections'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import breakpoints from 'theme/breakpoints'
import { Column } from 'styled/alignment/Column'
import SponsorItemCard from './SponsorItemCard'
import SponsorItemForm from './SponsorItemForm'
import { sizeOptions, getItemLabel } from './SponsorSectionForm'

export default function SponsorSectionCard({
    section,
    canMoveUp,
    canMoveDown,
    onMoveUp,
    onMoveDown,
    onEditSection,
    onDeleteSection,
    onSuccess,
    toast,
}) {
    const { event } = useEvent()
    const [modalItem, setModalItem] = useState(false)
    const [deletingItem, setDeletingItem] = useState(false)

    const items = section?.items || []
    const sizeLabel = sizeOptions.find(option => option.value === section?.logo_size)?.label
    const itemLabel = getItemLabel(section?.type)

    function toggleModalItem(action) {
        setModalItem(action)
    }

    function handleItemSuccess(type) {
        setModalItem(false)
        if (type === 'createItem') {
            toast.success('Item adicionado com sucesso!', { position: 'top-center' })
        }
        if (type === 'editItem') {
            toast.success('Item atualizado com sucesso!', { position: 'top-center' })
        }
        onSuccess()
    }

    async function deleteItem(itemId) {
        setDeletingItem(true)
        try {
            await sponsorSectionsApi.deleteItem(event.slug, section.id, itemId)
            setModalItem(false)
            toast.success('Item excluído com sucesso!', { position: 'top-center' })
            onSuccess()
        } catch (error) {
        } finally {
            setDeletingItem(false)
        }
    }

    async function moveItem(item, direction) {
        const index = items.findIndex(i => i.id === item.id)
        const targetIndex = direction === 'up' ? index - 1 : index + 1
        const target = items[targetIndex]

        if (!target) return

        try {
            await Promise.all([
                sponsorSectionsApi.updateItem(event.slug, section.id, item.id, { sort_order: target.sort_order }),
                sponsorSectionsApi.updateItem(event.slug, section.id, target.id, { sort_order: item.sort_order }),
            ])
            onSuccess()
        } catch (error) {
        }
    }

    return (
        <Card>
            <CardHeader>
                <HeaderLeft>
                    <Text name='highlight' type='bold' color={colors.nightDark} paddingBottom={0}>
                        {section?.display_title}
                    </Text>
                    <SizeBadge>Logos: {sizeLabel}</SizeBadge>
                </HeaderLeft>

                <ActionsRow>
                    <IconButton disabled={!canMoveUp} onClick={() => canMoveUp && onMoveUp()}>
                        <ChevronUp size={16} color={canMoveUp ? colors.night : colors.nightLight} />
                    </IconButton>
                    <IconButton disabled={!canMoveDown} onClick={() => canMoveDown && onMoveDown()}>
                        <ChevronDown size={16} color={canMoveDown ? colors.night : colors.nightLight} />
                    </IconButton>
                    <IconButton onClick={onEditSection}>
                        <Edit size={16} color={colors.primaryLight} />
                    </IconButton>
                    <IconButton onClick={onDeleteSection}>
                        <Trash2 size={16} color={colors.danger} />
                    </IconButton>
                </ActionsRow>
            </CardHeader>

            <ItemsList>
                {items.map((item, index) => (
                    <SponsorItemCard
                        key={item.id}
                        item={item}
                        canMoveUp={index > 0}
                        canMoveDown={index < items.length - 1}
                        onMoveUp={() => moveItem(item, 'up')}
                        onMoveDown={() => moveItem(item, 'down')}
                        onEdit={() => toggleModalItem({ type: 'editItem', item })}
                        onDelete={() => toggleModalItem({ type: 'deleteItem', item })}
                    />
                ))}

                {items.length === 0 && (
                    <EmptyItems>Nenhum item cadastrado nessa seção ainda.</EmptyItems>
                )}
            </ItemsList>

            <AddItemButton type='button' onClick={() => toggleModalItem({ type: 'item' })}>
                <PlusSquare size={16} />
                Adicionar {itemLabel}
            </AddItemButton>

            {modalItem?.type === 'deleteItem' && (
                <Modal isOpen={modalItem?.type === 'deleteItem'} onClose={() => setModalItem(false)}>
                    <Text>Tem certeza que deseja excluir esse {itemLabel}?</Text>
                    {deletingItem ? (
                        <Loader />
                    ) : (
                        <ModalActions>
                            <Button onClick={() => setModalItem(false)} variant='light'>Cancelar</Button>
                            <Button onClick={() => deleteItem(modalItem?.item?.id)}>Confirmar</Button>
                        </ModalActions>
                    )}
                </Modal>
            )}

            {(modalItem?.type === 'item' || modalItem?.type === 'editItem') && (
                <SponsorItemForm
                    modalItem={modalItem}
                    edit={modalItem?.item}
                    sectionId={section.id}
                    itemLabel={itemLabel}
                    toast={toast}
                    setModalItem={setModalItem}
                    onSuccess={handleItemSuccess}
                />
            )}
        </Card>
    )
}

const Card = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 20px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 8px ${colors.night}08;
`;

const CardHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const SizeBadge = styled.span`
    font-size: ${fonts.sizes.mini};
    color: ${colors.night};
`;

const ActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const IconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
    opacity: ${({ disabled }) => disabled ? 0.4 : 1};

    &:hover:not(:disabled) {
        background: ${colors.neutralDark};
    }
`;

const ItemsList = styled(Column)`
    width: 100%;
    gap: 8px;
`;

const EmptyItems = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    padding: 10px 0;
`;

const AddItemButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    margin-top: 16px;
    padding: 12px 16px;
    border: 1.5px dashed ${colors.occasionalPurple}55;
    border-radius: 10px;
    background: ${colors.occasionalPurple}08;
    color: ${colors.occasionalPurple};
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${colors.occasionalPurple}14;
        border-color: ${colors.occasionalPurple};
    }
`;

const ModalActions = styled(Column)`
    margin-top: 15px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 5px;
    }
`;
